import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Alert from '@material-ui/lab/Alert';
import { useWindowWidth } from '@react-hook/window-size';
import sanitizeHtml from 'sanitize-html';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import DocumentDetailsDialog from '../../dialogs/DocumentDetailsDialog/DocumentDetailsDialog';
import { ReduxState } from '../../redux/types';
import Company, { EmptyCompany } from '../../types/Company';
import Document from '../../types/Document';
import api from '../../utils/api';
import DocumentCard from './components/DocumentCard/DocumentCard';
import { FlexContainer, PlaceholderCard, StyledGridList, Title } from './styled';

interface Props {
    publicPage?: boolean;
}

const DocumentsPage = (props: Props) => {
    const { publicPage } = props;

    const { companyId } = useParams<{ companyId: string }>();

    const userInfo = useSelector((state: ReduxState) => state.user);
    const userType = userInfo.type;
    const isAdmin = userType === 'admin';
    const realCompanyId = companyId || userInfo.companyId;

    const { t } = useTranslation();

    const languageCode = useMemo(() => {
        return t('language_code');
    }, [t]);

    const windowWidth = useWindowWidth();
    const titleSuffix = windowWidth < 900 ? '_small' : '';

    const [isLoading, setIsLoading] = useState(true);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

    const [companyData, setCompanyData] = useState<Company>(EmptyCompany);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [selectedDocumentId, selectDocumentId] = useState(0);
    const [documentsInfo, setDocumentsInfo] = useState({
        maxUploadSize: 0,
        allowedCategories: {
            admin: [],
            auditor: [],
            customer: [],
            certifier: []
        }
    });

    const basePath = publicPage ? '/public_documents' : ('/companies/' + realCompanyId + '/documents');

    const getDocuments = useCallback(() => {
        api.request(basePath).then((res) => {
            setDocuments(res);
            setIsLoading(false);
        });
    }, [basePath]);

    const getData = useCallback(() => {
        api.request(basePath + '/info').then((res) => {
            setDocumentsInfo(res);
        });

        if (realCompanyId) {
            api.request('/companies/' + realCompanyId).then((companyRes) => {
                setCompanyData(companyRes);
                getDocuments();
            });
        } else {
            getDocuments();
        }
    }, [basePath, getDocuments, realCompanyId]);

    const openDetailsDialog = useCallback((documentId: number) => {
        selectDocumentId(documentId);
        setIsDetailsDialogOpen(true);
    }, []);

    const closeDetailsDialog = useCallback(() => {
        setIsDetailsDialogOpen(false);
    }, []);

    useEffect(() => {
        getData();
    }, [getData]);

    const documentYears = documents.reduce((prev: (number | null)[], curr: Document) => {
        if (!prev.includes(curr.year)) {
            return [...prev, curr.year];
        }

        return prev;
    }, []).sort((n1, n2) => {
        if (n1 === null) {
            return 1;
        }

        if (n2 === null) {
            return -1;
        }

        return n2 - n1;
    });

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>
                    {(companyId && !publicPage) ? (
                        <Trans i18nKey={'pages.documents.title_with_company' + titleSuffix} tOptions={{ company: companyData.name }}>
                            _<small>{companyData.name}</small>_
                        </Trans>
                    ) : t('pages.' + (publicPage ? 'public_documents' : 'documents') + '.title' + titleSuffix)}
                </Title>

                {(!publicPage || isAdmin) && (userType !== 'certifier') && (
                    <GreenButton variant='outlined' onClick={() => openDetailsDialog(0)} >
                        {t('pages.documents.new_document')}
                    </GreenButton>
                )}
            </FlexContainer>

            {!publicPage && (
                <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='info'>
                    <div dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(t('pages.documents.help'), {
                            allowedAttributes: {
                                'p': ['style'],
                            },
                            allowedStyles: {
                                'p': {
                                    'margin-top': [/^\d+$/],
                                    'margin-bottom': [/^\d+$/]
                                }
                            }
                        }
                        )
                    }} />
                </Alert>
            )}

            {documentYears.map(year => {
                return (
                    <>
                        {!publicPage && (
                            <p style={{ display: 'block', fontSize: '1.45em', marginBottom: 0, marginLeft: '6px' }}>
                                {year === null ? t('pages.documents.undefined_year') : t('pages.documents.year_title', { year })}
                            </p>
                        )}

                        <StyledGridList>
                            {documents.filter(d => d.year === year).filter((d: any) => {
                                if (!publicPage || isAdmin) {
                                    return true;
                                }

                                if (languageCode === 'it-IT' && (!d.language || d.language.code === languageCode)) {
                                    return true;
                                }

                                if (languageCode !== 'it-IT' && (!d.language || d.language.code === languageCode || d.language.code.toLowerCase().includes('gb') || d.language.code.toLowerCase().includes('en'))) {
                                    return true;
                                }

                                return false;
                            }).map(document => {
                                return (
                                    <DocumentCard
                                        document={document}
                                        openEditDialog={() => openDetailsDialog(document.id)}
                                        updateData={getDocuments}
                                        key={document.id}
                                        basePath={basePath}
                                        publicPage={publicPage}
                                        isAdmin={isAdmin}
                                    />
                                );
                            })}

                            {
                                // displays some placeholder cards to don't break the grid layout
                                [...Array(Math.ceil(windowWidth / 300)).keys()].map(item => {
                                    return (
                                        <PlaceholderCard key={'placeholder_' + item} />
                                    );
                                })
                            }

                        </StyledGridList>
                    </>
                );
            })}

            <DocumentDetailsDialog
                basePath={basePath}
                open={isDetailsDialogOpen}
                onClose={closeDetailsDialog}
                companyId={realCompanyId}
                documentId={selectedDocumentId}
                categories={documentsInfo?.allowedCategories ? documentsInfo?.allowedCategories[userType] : []}
                maxUploadSize={documentsInfo.maxUploadSize}
                updateData={getDocuments}
                publicPage={publicPage}
                isAdmin={isAdmin}
            />
        </>
    );
};

export default DocumentsPage;
